/*
 * Text date/time fields use src/date-entry.ts; avoid native controls' locale and silent coercion.
 * Design: docs/2026-10-05-date-time-components.md. Typing is sufficient; pickers are optional.
 * Echo the parsed weekday/month, and zone/dated offset for date-times; normalize text on blur.
 * onChange receives the stored native-format string or empty string. Refused text stays for
 * correction with a reason on blur and never reaches the form. Invalid or owed-empty input blocks
 * submission. No default-to-now or value from focus, taps, opening/closing, arrows or wheel; a
 * day without a required time is incomplete. Callers supply label htmlFor; styles are in styles.css.
 */
import {
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { DayPicker } from "react-day-picker";
import {
  appWeekdayDayMonth,
  isTimeZone,
  offsetLabel,
  resolveWallClock,
  viewerTimeZone,
  zoneClock,
} from "../src/app-dates.ts";
import {
  MONTH_NAMES,
  type Reading,
  readDate,
  readDateTime,
  readTime,
  showDate,
  showDateTime,
  showTime,
  splitDateTime,
} from "../src/date-entry.ts";
import { BackIcon, CalendarIcon, ClockIcon, ForwardIcon } from "./icons.tsx";
import { type Box, placePopover } from "./popover-placement.ts";

/** A bound a reading must respect, with the sentence the rep reads when it does not. */
export type Limit = { readonly value: string; readonly message: string };

type FieldProps = {
  /** The input's id, which the call site's `<label htmlFor>` names. */
  readonly id: string;
  /** The stored value, or `""`. */
  readonly value: string;
  /** Handed the stored value for a good entry and `""` for anything else. */
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean | undefined;
  readonly required?: boolean;
  readonly name?: string;
  /** Moves focus into the field when it appears, for a form that opens in place of its button. */
  readonly autoFocus?: boolean;
  /** The id of a hint the call site already shows, read with the field. */
  readonly describedBy?: string;
  /** Why an empty box is a fault, when it is one. See `Owed`. */
  readonly owed?: Owed | undefined;
  /** The instant to read "today" and "the past" against. Tests pin it; the app leaves it out. */
  readonly now?: number | undefined;
};

/**
 * Why an empty box may not stay empty: the other half of its pair is filled. The form is stopped
 * from the moment that is so; `shown` says whether to say it beside the box yet, which waits until
 * the rep has left the pair, so nobody is told off for the second half while typing the first.
 */
export type Owed = { readonly message: string; readonly shown: boolean };

const refused = (message: string): Reading => ({ kind: "refused", message });

/*
 * Hold blur-driven layout changes until the press that moved focus ends: inserting a reason
 * between pointer-down and pointer-up can move Save away and lose the click. Form validity still
 * updates immediately from the keystroke; only layout waits.
 */
let pressed = false;
/**
 * How many presses have begun, which tells one press from the next. A line or a box is held only
 * by a press it was on the page to see begin: one that began earlier says nothing of how the page
 * looked to it then, and its release may never be heard (the press that closed the card the last
 * field was in, whose listeners left with it). So whether a press holds anything is its own to
 * tell, and never hangs on what else has or has not left the page.
 */
let presses = 0;
const awaitingRelease: (() => void)[] = [];
/**
 * The one timer that gives up on a press whose release never arrives. Which gestures lose their
 * release has not been measured in a browser; a press ended in another window, or over a menu the
 * browser drew, are the likely ones. It runs from the press itself and not from the first line to
 * wait on it, or a press nothing waited on would stay on for good and hold the next field left by
 * keyboard, however much later.
 */
let overdue: ReturnType<typeof setTimeout> | null = null;
const GIVEN_UP_AFTER_MS = 1500;
function began(): void {
  // A timer armed for an earlier press must not let go of this one.
  clearOverdue();
  pressed = true;
  presses += 1;
  overdue = setTimeout(released, GIVEN_UP_AFTER_MS);
}
function released(): void {
  clearOverdue();
  pressed = false;
  // After this turn, in which the browser also sends the click the release completes.
  setTimeout(() => {
    for (const run of awaitingRelease.splice(0)) run();
  }, 0);
}
function clearOverdue(): void {
  if (overdue !== null) clearTimeout(overdue);
  overdue = null;
}
/**
 * How many mounted lines and boxes are watching for presses. It decides only when to stop
 * listening: a page cut off without being unmounted (as the suite cuts off the real one) never
 * counts its own down, so nothing shown may depend on this reaching zero.
 */
let watching = 0;
/**
 * Presses are listened for by the lines that are held, for as long as one is on the page, and not
 * by this module as it loads: whatever takes the document's listeners away (another page mounted
 * and cut off in the same process, as the suite does) would otherwise leave every later line
 * unheld for good. Adding a listener the document already has adds nothing.
 */
function watchPresses(): () => void {
  document.addEventListener("pointerdown", began, true);
  document.addEventListener("pointerup", released, true);
  document.addEventListener("pointercancel", released, true);
  watching += 1;
  return () => {
    watching -= 1;
    if (watching > 0) return;
    document.removeEventListener("pointerdown", began, true);
    document.removeEventListener("pointerup", released, true);
    document.removeEventListener("pointercancel", released, true);
  };
}
/** Whether a press now in progress holds the caller, which listens for presses while mounted. */
function usePressHold(): () => boolean {
  // The presses begun before the caller was on the page: none of them holds it.
  const earlier = useRef(presses);
  useLayoutEffect(watchPresses, []);
  return () => pressed && presses !== earlier.current;
}
/**
 * A line under a field as it was when a press began, for as long as the press lasts.
 *
 * Not every line that appears is the field's own doing on blur: a zone taken as the picker loses
 * focus puts a description under the picker and "Warsaw time" under every slot that reads on that
 * clock. So the lines themselves are held, whatever changed them, and catch up after the release.
 */
export function useHeldWhilePressed<T>(value: T): T {
  const shown = useRef(value);
  const [, again] = useState(0);
  const holds = usePressHold();
  const held = holds();
  if (!held) shown.current = value;
  const behind = held && !Object.is(shown.current, value);
  useEffect(() => {
    if (behind) afterPress(() => again((count) => count + 1));
  }, [behind]);
  return shown.current;
}

function afterPress(run: () => void): void {
  if (!pressed) {
    run();
    return;
  }
  awaitingRelease.push(run);
}

/** The calendar day in a zone, falling back to the viewer's own zone and then to UTC. */
function todayIn(zone: string | undefined, now: number): string {
  const clock = zoneClock(zone && isTimeZone(zone) ? zone : viewerTimeZone(), now);
  return clock?.date ?? new Date(now).toISOString().slice(0, 10);
}

/** "Warsaw" for `Europe/Warsaw`: the word a rep calls the zone by. */
export const zoneCity = (zone: string): string =>
  (zone.split("/").at(-1) ?? zone).replaceAll("_", " ");

/**
 * The text in the box, what it reads as, and what the form has been handed.
 *
 * The box is the source while the rep types; the form's value is the source when the form itself
 * changes it (a proposal chosen, a form reset). `handed` is the last value this field gave or was
 * given and `seen` the last one the form showed it, which is how the two are told apart: a value
 * that arrives changed and is not what the field just handed over came from the form. A form that
 * ignores what it is handed changes nothing, and the box keeps what was typed.
 */
function useEntry(options: {
  value: string;
  onChange: (value: string) => void;
  read: (text: string) => Reading;
  show: (value: string) => string;
  owed: Owed | undefined;
}) {
  const { value, onChange, read, show } = options;
  const [text, setText] = useState(() => show(value));
  const [focused, setFocused] = useState(false);
  // A problem shown once stays while the rep corrects it, so the reason does not blink away.
  const [kept, setKept] = useState(false);
  const handed = useRef(value);
  const seen = useRef(value);
  const pressHolds = usePressHold();
  const reading = read(text);
  const good = reading.kind === "value" ? reading.value : "";
  const message =
    reading.kind === "refused" || reading.kind === "incomplete" ? reading.message : null;
  const owed = reading.kind === "empty" && options.owed ? options.owed : null;
  /** Why the form may not be sent with the box as it stands, whether or not it is shown yet. */
  const fault = message ?? owed?.message ?? null;

  // Before the browser paints or handles another event, so the form, and the other half of a
  // pair, never lag a keystroke behind the box: a Save pressed at once still meets the fault.
  useLayoutEffect(() => {
    const fromTheForm = value !== seen.current && value !== handed.current;
    seen.current = value;
    if (fromTheForm) {
      handed.current = value;
      if (value !== good) setText(show(value));
      return;
    }
    if (good !== handed.current) {
      handed.current = good;
      onChange(good);
    }
  }, [value, good, onChange, show]);

  return {
    text,
    good,
    focused,
    fault,
    /** The reason to show now: never while a first attempt is still being typed. */
    problem:
      message !== null
        ? !focused || kept
          ? message
          : null
        : owed !== null && (kept || (!focused && owed.shown))
          ? owed.message
          : null,
    /** The browser refused to send the form because of this box: say why at once. */
    reveal() {
      setKept(true);
    },
    write(next: string) {
      setText(next);
      const after = read(next);
      if (after.kind === "value" || after.kind === "empty") setKept(false);
    },
    onFocus() {
      setFocused(true);
    },
    onBlur(box: HTMLElement) {
      const left = (): void => {
        // The press that took the focus gave it straight back: the rep has not left.
        if (document.activeElement === box) return;
        setFocused(false);
        setKept(fault !== null);
        // The box now spells the value the way the app writes it, which is itself the check.
        if (reading.kind === "value") setText(show(reading.value));
      };
      if (pressHolds()) afterPress(left);
      else left();
    },
  };
}

type Entry = ReturnType<typeof useEntry>;

/** Intersect all clipping ancestors with the window; the nearest pane alone can overstate room. */
function roomAround(element: HTMLElement): Box {
  let room: Box = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (style.overflowX === "visible" && style.overflowY === "visible") continue;
    const box = parent.getBoundingClientRect();
    // The client box: a scrollbar is not room.
    const left = box.left + parent.clientLeft;
    const top = box.top + parent.clientTop;
    // Nor is what a pane keeps clear at its foot for something fixed over it (the phone's sheet handle).
    const kept = Number.parseFloat(style.scrollPaddingBottom);
    room = {
      left: Math.max(room.left, left),
      top: Math.max(room.top, top),
      right: Math.min(room.right, left + parent.clientWidth),
      bottom: Math.min(room.bottom, top + parent.clientHeight - (Number.isFinite(kept) ? kept : 0)),
    };
  }
  return room;
}

/**
 * The nearest ancestor that has somewhere to scroll to. Asked once the popover is in the page: a
 * card that only clips computes to the same `overflow` as the pane that scrolls, and the pane may
 * have nothing to scroll until the popover has lengthened it.
 */
function scrollerOf(element: HTMLElement): HTMLElement | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY !== "auto" && overflowY !== "scroll") continue;
    if (parent.scrollHeight > parent.clientHeight) return parent;
  }
  return null;
}

interface PopoverEdge {
  /** False for the renders in which the popover is only being measured. */
  readonly placed: boolean;
  /** The room is narrower than the popover as it first measured: it has to take its narrow form. */
  readonly narrow: boolean;
  readonly end: boolean;
  readonly above: boolean;
  readonly shift: number;
  /** The room's width in px once measured: the popover is never wider than what it opens into. */
  readonly maxWidth: number | null;
}

const UNPLACED: PopoverEdge = {
  placed: false,
  narrow: false,
  end: false,
  above: false,
  shift: 0,
  maxWidth: null,
};
const UNPLACED_NARROW: PopoverEdge = { ...UNPLACED, narrow: true };

/**
 * Where a field's popover opens, decided once it has a size and before it is shown: inside the
 * room it has (`placePopover`), above the field only when it fits there and not below.
 *
 * `placed` is false while the popover is measured. A caller whose popover takes focus as it opens
 * must keep it out of the page for those renders (`data-measuring`), because a popover laid out
 * below a field at the foot of its pane makes the pane scroll to it as it takes focus, and the
 * place then chosen above the field is above where the field no longer is.
 *
 * A popover is held to its room's width before it is measured, so one that can give way does (the
 * list of times beside the month is 2px too wide for the Work panel at 1280 and 1440 and gives up
 * that much). One whose content still does not fit is measured again with `narrow` set, for a
 * caller that has a narrower form to give it (`data-narrow`).
 */
export function usePopoverEdge(
  open: boolean,
  root: RefObject<HTMLElement | null>,
  panel: RefObject<HTMLElement | null>,
): PopoverEdge {
  const [edge, setEdge] = useState<PopoverEdge>(UNPLACED);
  // What the pane still has to scroll by once the popover is in the page and has lengthened it.
  const owed = useRef(0);
  const { placed, narrow } = edge;
  useLayoutEffect(() => {
    if (!open) {
      owed.current = 0;
      setEdge(UNPLACED);
      return;
    }
    if (placed || !panel.current || !root.current) return;
    const whole = root.current.getBoundingClientRect();
    // It hangs under the box, over the line that says back what was read, so the box is the field.
    const box = root.current.querySelector(".lcr-entry-box")?.getBoundingClientRect() ?? whole;
    const field = { left: whole.left, top: whole.top, right: whole.right, bottom: box.bottom };
    const room = roomAround(root.current);
    const maxWidth = room.right - room.left;
    // Set here and not only in the next render: it has to hold while this pass measures.
    panel.current.style.maxWidth = `${maxWidth}px`;
    if (!narrow && panel.current.scrollWidth > panel.current.clientWidth) {
      setEdge(UNPLACED_NARROW);
      return;
    }
    const size = { width: panel.current.offsetWidth, height: panel.current.offsetHeight };
    const placement = placePopover(field, size, room);
    owed.current = placement.overflowBelow;
    setEdge({
      placed: true,
      narrow,
      // For a popover as wide as its field, which is hung from one end or the other.
      end: placement.shift < 0,
      above: placement.above,
      shift: placement.shift,
      maxWidth,
    });
  }, [open, placed, narrow, root, panel]);
  useLayoutEffect(() => {
    if (!placed || owed.current <= 0 || !root.current) return;
    // It fits on neither side: the pane makes up the difference, and no more than that.
    const scroller = scrollerOf(root.current);
    const target = scroller ? scroller.scrollTop + owed.current : 0;
    owed.current = 0;
    if (!scroller) return;
    scroller.scrollTop = target;
    if (scroller.scrollTop >= target - 0.5) return;
    /*
     * The popover opens 4px above its place and settles into it, and until it has, the pane is
     * that much shorter than it will be and stops the scroll short of the popover's foot (measured
     * on the phone layout: 4px left outside the pane). The rest is made up once it has settled.
     */
    const settled = (): void => {
      scroller.scrollTop = Math.max(scroller.scrollTop, target);
    };
    const opened = panel.current;
    opened?.addEventListener("animationend", settled, { once: true });
    return () => opened?.removeEventListener("animationend", settled);
  }, [placed, root, panel]);
  return edge;
}

/** The picker behind a field's button: open or not, and where focus goes when it closes. */
function usePicker() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const edge = usePopoverEdge(open, root, panel);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent): void => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  return {
    open,
    root,
    input,
    opener,
    panel,
    edge,
    show: () => setOpen(true),
    toggle: () => setOpen((current) => !current),
    /*
     * Escape from the box goes back to the box. Everything done inside the picker ends on its
     * button: a choice made by touch must not land in the box and raise the phone's keyboard over
     * the value just chosen.
     */
    close(focus: "field" | "button") {
      setOpen(false);
      (focus === "field" ? input.current : opener.current)?.focus();
    },
  };
}

type Picker = ReturnType<typeof usePicker>;

/** The box, its button, the line that says back, the reason for a refusal, and the picker. */
function Field(props: {
  field: FieldProps;
  entry: Entry;
  picker: Picker;
  placeholder: string;
  inputMode: "text" | "numeric";
  pickerName: string;
  pickerKind: "dialog" | "listbox";
  icon: ReactNode;
  /** What the field says back under the box, or `null` for nothing. */
  note: string | null;
  children: ReactNode;
}) {
  const { field, entry, picker } = props;
  const noteId = useId();
  const problemId = useId();
  const panelId = useId();
  // The picker is how the rep is finishing the entry, so the half-made value is not yet a fault.
  const problem = useHeldWhilePressed(picker.open ? null : entry.problem);
  const note = useHeldWhilePressed(props.note);
  const described = [field.describedBy, note ? noteId : null, problem ? problemId : null]
    .filter(Boolean)
    .join(" ");

  const fieldFocus = picker.input;
  useEffect(() => {
    if (field.autoFocus) fieldFocus.current?.focus();
  }, [field.autoFocus, fieldFocus]);
  /*
   * The browser's own check carries the fault, shown or not. A box holding text that is no value
   * hands the form "", and a form that only looks at what it was handed would send around it; the
   * native control stopped the form on bad input, and this is that guarantee kept.
   */
  const { fault } = entry;
  useLayoutEffect(() => {
    fieldFocus.current?.setCustomValidity(fault ?? "");
  }, [fault, fieldFocus]);

  const keys = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" && !picker.open) {
      event.preventDefault();
      picker.show();
    } else if (event.key === "Escape" && picker.open) {
      event.preventDefault();
      event.stopPropagation();
      picker.close("field");
    }
  };

  return (
    <div className="lcr-entry" ref={picker.root}>
      <div
        className="lcr-entry-box"
        data-invalid={problem ? "" : undefined}
        data-disabled={field.disabled ? "" : undefined}
      >
        <input
          ref={picker.input}
          id={field.id}
          name={field.name}
          type="text"
          className="lcr-entry-input"
          inputMode={props.inputMode}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={props.placeholder}
          disabled={field.disabled}
          required={field.required}
          value={entry.text}
          aria-invalid={problem ? true : undefined}
          aria-describedby={described || undefined}
          onChange={(event) => entry.write(event.target.value)}
          onFocus={entry.onFocus}
          onBlur={(event) => entry.onBlur(event.currentTarget)}
          onInvalid={(event) => {
            /*
             * The browser has refused to send the form because of this box. The line under the box
             * says why, in the app's own words and dress, so the browser's bubble is declined; it
             * would say the same thing again on top of it. Declining it also declines the focus
             * the browser would have given, so the first box at fault takes it here.
             */
            event.preventDefault();
            entry.reveal();
            const box = event.currentTarget;
            const first = Array.from(box.form?.elements ?? []).find(
              (control) => control instanceof HTMLInputElement && !control.validity.valid,
            );
            if (first === box) box.focus();
          }}
          onKeyDown={keys}
        />
        <button
          ref={picker.opener}
          type="button"
          className="lcr-entry-pick"
          aria-label={props.pickerName}
          aria-haspopup={props.pickerKind}
          aria-expanded={picker.open}
          aria-controls={picker.open ? panelId : undefined}
          disabled={field.disabled}
          onClick={picker.toggle}
        >
          {props.icon}
        </button>
      </div>
      {note && (
        <p className="lcr-entry-note" id={noteId} role="status">
          {note}
        </p>
      )}
      {problem && (
        <p className="lcr-entry-problem" id={problemId} role="alert">
          {problem}
        </p>
      )}
      {picker.open && (
        // biome-ignore lint/a11y/noStaticElementInteractions: Escape and Tab leave the picker from any control inside it
        <div
          ref={picker.panel}
          id={panelId}
          className="lcr-popover lcr-entry-popover"
          data-measuring={picker.edge.placed ? undefined : ""}
          data-narrow={picker.edge.narrow ? "" : undefined}
          data-above={picker.edge.above ? "" : undefined}
          style={{
            left: picker.edge.shift,
            ...(picker.edge.maxWidth === null ? {} : { maxWidth: picker.edge.maxWidth }),
          }}
          onKeyDown={(event) => {
            if (event.key !== "Escape" && event.key !== "Tab") return;
            if (event.key === "Escape") event.preventDefault();
            event.stopPropagation();
            picker.close("button");
          }}
        >
          {/*
           * Mounted again once placed: what takes focus as it appears (the calendar, the list of
           * times) must do so where the picker is shown, not where it was measured.
           */}
          <Fragment key={picker.edge.placed ? "placed" : "measuring"}>{props.children}</Fragment>
        </div>
      )}
    </div>
  );
}

/** What the field says back while the rep types something it will rewrite on leaving. */
const saidBack = (entry: Entry, shown: string): string | null =>
  entry.focused && shown !== "" && shown !== entry.text.trim() ? shown : null;

/*
 * `react-day-picker` works in `Date` objects in the browser's own zone. The fields work in stored
 * calendar days, which have no zone. Noon keeps the crossing exact: no zone's clock change moves
 * noon to another day.
 */
const asLocalDate = (day: string): Date => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1, 12);
};
const asStoredDay = (date: Date): string =>
  `${String(date.getFullYear()).padStart(4, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const monthOf = (date: Date): string => `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
/** Indexed the way `Date.getDay` counts, Sunday first. */
const DAY_LETTERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

const CALENDAR_CLASSES = {
  root: "lcr-cal",
  months: "lcr-cal-months",
  month: "lcr-cal-month",
  month_caption: "lcr-cal-caption",
  caption_label: "lcr-cal-title",
  nav: "lcr-cal-nav",
  button_previous: "lcr-cal-step",
  button_next: "lcr-cal-step",
  chevron: "lcr-cal-chevron",
  month_grid: "lcr-cal-grid",
  weekdays: "lcr-cal-weekdays",
  weekday: "lcr-cal-weekday",
  weeks: "lcr-cal-weeks",
  week: "lcr-cal-week",
  day: "lcr-cal-day",
  day_button: "lcr-cal-day-button",
  selected: "lcr-cal-selected",
  today: "lcr-cal-today",
  outside: "lcr-cal-outside",
  disabled: "lcr-cal-disabled",
  hidden: "lcr-cal-hidden",
  focused: "lcr-cal-focused",
} as const;

/** One month, Monday first, in the app's own words. Days before `min` cannot be chosen. */
function Calendar(props: {
  selected: string;
  today: string;
  min: string | undefined;
  onPick: (day: string) => void;
}) {
  const [month, setMonth] = useState(() => asLocalDate(props.selected || props.today));
  return (
    <DayPicker
      mode="single"
      selected={props.selected ? asLocalDate(props.selected) : undefined}
      onSelect={(_selected, day) => props.onPick(asStoredDay(day))}
      month={month}
      onMonthChange={setMonth}
      today={asLocalDate(props.today)}
      disabled={props.min ? { before: asLocalDate(props.min) } : undefined}
      weekStartsOn={1}
      showOutsideDays
      fixedWeeks
      autoFocus
      classNames={CALENDAR_CLASSES}
      formatters={{
        formatCaption: monthOf,
        formatWeekdayName: (date) => DAY_LETTERS[date.getDay()] ?? "",
      }}
      labels={{
        labelGrid: monthOf,
        labelPrevious: () => "Previous month",
        labelNext: () => "Next month",
        labelDayButton: (date, modifiers) =>
          `${showDate(asStoredDay(date))}${modifiers["today"] ? ", today" : ""}${modifiers["selected"] ? ", chosen" : ""}`,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? <BackIcon size={16} /> : <ForwardIcon size={16} />,
      }}
    />
  );
}

const HALF_HOURS: readonly string[] = Array.from(
  { length: 48 },
  (_, index) => `${String(Math.floor(index / 2)).padStart(2, "0")}:${index % 2 ? "30" : "00"}`,
);

/**
 * The half hours of a day, to choose one from. Arrows move, Home and End jump, Enter chooses.
 *
 * It opens on the chosen time, or on the working day's start when none is: nobody wants to scroll
 * from midnight to say nine.
 */
function TimeList(props: {
  selected: string;
  name: string;
  /** Takes focus as it opens, when it is the whole picker and not beside a calendar. */
  lead: boolean;
  onPick: (time: string) => void;
}) {
  const list = useRef<HTMLDivElement>(null);
  const start =
    HALF_HOURS.findLast((time) => props.selected !== "" && time <= props.selected) ?? "09:00";
  const lead = useRef(props.lead);
  useEffect(() => {
    const box = list.current;
    const first = box?.querySelector<HTMLElement>("[tabindex='0']");
    if (!box || !first) return;
    // Set by hand: scrollIntoView would move the page under the rep as well as the list.
    box.scrollTop = first.offsetTop - box.clientHeight / 2 + first.offsetHeight / 2;
    if (lead.current) first.focus();
  }, []);
  const move = (event: KeyboardEvent<HTMLDivElement>): void => {
    const options = [...(list.current?.querySelectorAll<HTMLElement>("[role=option]") ?? [])];
    const focused = document.activeElement;
    const index = focused instanceof HTMLElement ? options.indexOf(focused) : -1;
    const target =
      event.key === "ArrowDown"
        ? options[Math.min(index + 1, options.length - 1)]
        : event.key === "ArrowUp"
          ? options[Math.max(index - 1, 0)]
          : event.key === "Home"
            ? options[0]
            : event.key === "End"
              ? options.at(-1)
              : undefined;
    if (!target) return;
    event.preventDefault();
    target.focus();
  };
  return (
    <div
      ref={list}
      className="lcr-entry-times"
      role="listbox"
      aria-label={props.name}
      tabIndex={-1}
      onKeyDown={move}
    >
      {HALF_HOURS.map((time) => (
        <button
          key={time}
          type="button"
          role="option"
          aria-selected={time === props.selected}
          tabIndex={time === start ? 0 : -1}
          className="lcr-entry-time"
          onClick={() => props.onPick(time)}
        >
          {time}
        </button>
      ))}
    </div>
  );
}

/** A calendar day: a follow-up date. Stored as `2026-10-12`. */
export function DateField(
  props: FieldProps & {
    /** Whose "today" a relative entry means. Left out, the viewer's own. */
    readonly zone?: string | undefined;
    /** The earliest day allowed, itself included. */
    readonly min?: Limit | undefined;
    /** Refuses a day before today: a follow-up cannot be due yesterday. */
    readonly notPast?: boolean;
  },
) {
  const today = todayIn(props.zone, props.now ?? Date.now());
  const min =
    props.min ??
    (props.notPast
      ? { value: today, message: `That day has passed. Today is ${showDate(today)}.` }
      : undefined);
  const read = (text: string): Reading => {
    const reading = readDate(text, { today });
    return reading.kind === "value" && min && reading.value < min.value
      ? refused(min.message)
      : reading;
  };
  const entry = useEntry({
    value: props.value,
    onChange: props.onChange,
    read,
    show: showDate,
    owed: props.owed,
  });
  const picker = usePicker();
  const plain = readDate(entry.text, { today });
  return (
    <Field
      field={props}
      entry={entry}
      picker={picker}
      placeholder="e.g. 12.10 or tomorrow"
      inputMode="text"
      pickerName="Choose from a calendar"
      pickerKind="dialog"
      icon={<CalendarIcon size={18} />}
      note={saidBack(entry, showDate(entry.good))}
    >
      <div role="dialog" aria-label="Choose a day" className="lcr-entry-dialog">
        <Calendar
          selected={plain.kind === "value" ? plain.value : ""}
          today={today}
          min={min?.value}
          onPick={(day) => {
            entry.write(showDate(day));
            picker.close("button");
          }}
        />
      </div>
    </Field>
  );
}

/** A time of day: the working hours pair. Stored as `14:00`. */
export function TimeField(
  props: FieldProps & {
    /** The time this one must come after, itself excluded: an Until after its From. */
    readonly after?: Limit | undefined;
  },
) {
  const { after } = props;
  const read = (text: string): Reading => {
    const reading = readTime(text);
    return reading.kind === "value" && after && reading.value <= after.value
      ? refused(after.message)
      : reading;
  };
  const entry = useEntry({
    value: props.value,
    onChange: props.onChange,
    read,
    show: showTime,
    owed: props.owed,
  });
  const picker = usePicker();
  const plain = readTime(entry.text);
  return (
    <Field
      field={props}
      entry={entry}
      picker={picker}
      placeholder="e.g. 14:00"
      inputMode="numeric"
      pickerName="Choose a time"
      pickerKind="listbox"
      icon={<ClockIcon size={18} />}
      note={saidBack(entry, showTime(entry.good))}
    >
      <TimeList
        selected={plain.kind === "value" ? plain.value : ""}
        name="Times"
        lead
        onPick={(time) => {
          entry.write(time);
          picker.close("button");
        }}
      />
    </Field>
  );
}

/**
 * A day and a time on it, as a clock in `zone` shows it: a slot, an event's start, a booking.
 * Stored as `2026-10-12T14:00`, with the zone held beside it by the form.
 */
export function DateTimeField(
  props: FieldProps & {
    /** Whose clock this is. With none, the reading is taken as written and nothing is said of it. */
    readonly zone?: string | undefined;
    /** Refuses a reading earlier than now on that clock: a slot cannot start in the past. */
    readonly notPast?: boolean | undefined;
    /** Something more to say back under a good reading: when the slot it starts ends. */
    readonly remark?: string | undefined;
    /** The reading this one must come after, itself excluded: an end after its start. */
    readonly after?: Limit | undefined;
  },
) {
  const now = props.now ?? Date.now();
  const zone = props.zone && isTimeZone(props.zone) ? props.zone : "";
  const today = todayIn(zone, now);
  const { after, notPast } = props;

  const read = (text: string): Reading => {
    const reading = readDateTime(text, { today });
    if (reading.kind !== "value") return reading;
    const parts = splitDateTime(reading.value);
    if (zone !== "" && parts) {
      const resolved = resolveWallClock(reading.value, zone);
      const where = `${showDate(parts.date)} in ${zoneCity(zone)}`;
      if (resolved.kind === "never")
        return refused(
          `${parts.time} does not exist on ${where}: the clocks go straight from ${resolved.from} to ${resolved.to}.`,
        );
      if (resolved.kind === "twice")
        return refused(
          `${parts.time} happens twice on ${where}, when the clocks go back from ${resolved.to} to ${resolved.from}. Choose a time outside that hour.`,
        );
      const here = notPast ? zoneClock(zone, now) : null;
      if (here && reading.value < `${here.date}T${here.time}`)
        return refused(
          `That time has already passed. It is ${here.time} on ${showDate(here.date)} in ${zoneCity(zone)}.`,
        );
    }
    return after && reading.value <= after.value ? refused(after.message) : reading;
  };

  const entry = useEntry({
    value: props.value,
    onChange: props.onChange,
    read,
    show: showDateTime,
    owed: props.owed,
  });
  const picker = usePicker();

  // What the picker shows as chosen, read without the form's limits: a past day is still a day.
  const whole = readDateTime(entry.text, { today });
  const split = whole.kind === "value" ? splitDateTime(whole.value) : null;
  const dayOnly = readDate(entry.text, { today });
  const timeOnly = readTime(entry.text);
  const day = split?.date ?? (dayOnly.kind === "value" ? dayOnly.value : "");
  const time = split?.time ?? (timeOnly.kind === "value" ? timeOnly.value : "");

  const said = saidBack(entry, showDateTime(entry.good));
  const whose = entry.good === "" || zone === "" ? null : zoneNote(entry.good, zone);
  const remark = entry.good === "" ? null : props.remark;
  const note = [said, whose, remark].filter(Boolean).join(" · ") || null;

  const times = useRef<HTMLDivElement>(null);
  return (
    <Field
      field={props}
      entry={entry}
      picker={picker}
      placeholder="e.g. 12.10 14:00"
      inputMode="text"
      pickerName="Choose from a calendar"
      pickerKind="dialog"
      icon={<CalendarIcon size={18} />}
      note={note}
    >
      <div role="dialog" aria-label="Choose a day and a time" className="lcr-entry-dialog">
        <Calendar
          selected={day}
          today={today}
          // A day the field would refuse is not offered: with `notPast`, nothing before today on
          // the form's clock. With no zone picked there is no such clock and the field refuses
          // nothing, so no day is struck: the viewer's own today is a day off the zone they may
          // be about to pick.
          min={notPast && zone !== "" ? today : undefined}
          onPick={(picked) => {
            if (time === "") {
              entry.write(showDate(picked));
              // The day is half of it. The times are next, and the picker stays for them.
              times.current?.querySelector<HTMLElement>("[tabindex='0']")?.focus();
              return;
            }
            entry.write(showDateTime(`${picked}T${time}`));
            picker.close("button");
          }}
        />
        <div ref={times} className="lcr-entry-dialog-times">
          <TimeList
            selected={time}
            name="Times"
            lead={false}
            onPick={(picked) => {
              if (day === "") {
                entry.write(picked);
                return;
              }
              entry.write(showDateTime(`${day}T${picked}`));
              picker.close("button");
            }}
          />
        </div>
      </div>
    </Field>
  );
}

/**
 * Whose clock a reading is on, and what it is on the viewer's own when that differs: "Warsaw time,
 * UTC+2 · 13:00 your time". The offset is the zone's on that day, not today's.
 */
function zoneNote(value: string, zone: string): string | null {
  const resolved = resolveWallClock(value, zone);
  if (resolved.kind !== "once") return null;
  const whose =
    zone === "UTC" ? "UTC" : `${zoneCity(zone)} time, ${offsetLabel(resolved.offsetMinutes)}`;
  const mine = zoneClock(viewerTimeZone(), resolved.at);
  if (mine === null || mine.offsetMinutes === resolved.offsetMinutes) return whose;
  const sameDay = mine.date === value.slice(0, 10);
  return `${whose} · ${sameDay ? "" : `${appWeekdayDayMonth(mine.date)}, `}${mine.time} your time`;
}
